/**
 * Geometry Editing Store
 *
 * Zustand store for managing geometry editing state (Epic 2.3).
 * Handles zone creation, surface editing, fenestration placement,
 * adjacency detection, and construction management with full undo/redo support.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { useIDFStore } from '../stores/idfStore';
import type {
  EditMode,
  DrawingTool,
  SnapSettings,
  DrawingState,
  Point2D,
  FloorLevel,
  ZoneCreateConfig,
  SimpleBoxConfig,
  FootprintExtrudeConfig,
  FenestrationPlaceConfig,
  AdjacencyResult,
  AdjacencyDetectionSettings,
  ConstructionAssembly,
  MaterialLayer,
  GeometryAction,
  EditorSelection,
  EditorNotification,
  ZoneWizardState,
  FenestrationPlacerState,
  AdjacencyDetectorState,
  ConstructionBuilderState,
  SurfaceMoveConfig,
  SurfaceResizeConfig,
  SurfaceBoundaryConfig,
} from './editingTypes';
import {
  DEFAULT_SNAP_SETTINGS,
  DEFAULT_ADJACENCY_SETTINGS,
} from './editingTypes';
import type { Vector3, ZoneGeometry, SurfaceGeometry } from './types';

// Maximum history size for undo/redo
const MAX_HISTORY_SIZE = 50;

/**
 * Generate a unique ID for new objects.
 */
function generateId(prefix: string = 'geo'): string {
  return `${prefix}_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Geometry editing state interface.
 */
interface GeometryEditState {
  // ============================================================================
  // Core State
  // ============================================================================

  /** Current editing mode */
  editMode: EditMode;

  /** Current drawing tool (for 2D floor plan) */
  drawingTool: DrawingTool;

  /** Whether the editor is in editing mode vs view-only mode */
  isEditing: boolean;

  /** Selection state */
  selection: EditorSelection;

  /** Whether there are unsaved changes */
  isDirty: boolean;

  // ============================================================================
  // Drawing State (2D Floor Plan)
  // ============================================================================

  /** Current drawing state */
  drawing: DrawingState;

  /** Snap settings */
  snapSettings: SnapSettings;

  /** Available floor levels */
  floorLevels: FloorLevel[];

  /** Currently active floor level index */
  activeFloorIndex: number;

  // ============================================================================
  // Zone Creation Wizard
  // ============================================================================

  /** Zone wizard state */
  zoneWizard: ZoneWizardState;

  // ============================================================================
  // Fenestration Placement
  // ============================================================================

  /** Fenestration placer state */
  fenestrationPlacer: FenestrationPlacerState;

  // ============================================================================
  // Adjacency Detection
  // ============================================================================

  /** Adjacency detector state */
  adjacencyDetector: AdjacencyDetectorState;

  // ============================================================================
  // Construction Builder
  // ============================================================================

  /** Construction builder state */
  constructionBuilder: ConstructionBuilderState;

  /** Available constructions in the model */
  availableConstructions: ConstructionAssembly[];

  /** Available materials in the model */
  availableMaterials: string[];

  // ============================================================================
  // Undo/Redo
  // ============================================================================

  /** Undo stack */
  undoStack: GeometryAction[];

  /** Redo stack */
  redoStack: GeometryAction[];

  /** Whether undo is available */
  canUndo: boolean;

  /** Whether redo is available */
  canRedo: boolean;

  // ============================================================================
  // Notifications
  // ============================================================================

  /** Active notifications */
  notifications: EditorNotification[];

  // ============================================================================
  // Actions - Core
  // ============================================================================

  /** Enable editing mode */
  enableEditing: () => void;

  /** Disable editing mode */
  disableEditing: () => void;

  /** Set edit mode */
  setEditMode: (mode: EditMode) => void;

  /** Set drawing tool */
  setDrawingTool: (tool: DrawingTool) => void;

  /** Update selection */
  setSelection: (selection: Partial<EditorSelection>) => void;

  /** Clear selection */
  clearSelection: () => void;

  /** Add to selection */
  addToSelection: (type: 'zone' | 'surface' | 'fenestration', id: string) => void;

  /** Remove from selection */
  removeFromSelection: (type: 'zone' | 'surface' | 'fenestration', id: string) => void;

  // ============================================================================
  // Actions - Drawing
  // ============================================================================

  /** Start drawing a new polygon */
  startDrawing: () => void;

  /** Add a vertex to the current drawing */
  addDrawingVertex: (point: Point2D) => void;

  /** Update current mouse position for preview */
  updateDrawingPreview: (point: Point2D | null) => void;

  /** Close the current polygon */
  closeDrawing: () => void;

  /** Cancel current drawing */
  cancelDrawing: () => void;

  /** Update snap settings */
  setSnapSettings: (settings: Partial<SnapSettings>) => void;

  /** Set active floor level */
  setActiveFloor: (index: number) => void;

  /** Add a floor level */
  addFloorLevel: (floor: FloorLevel) => void;

  // ============================================================================
  // Actions - Zone Wizard
  // ============================================================================

  /** Open zone creation wizard */
  openZoneWizard: () => void;

  /** Close zone creation wizard */
  closeZoneWizard: () => void;

  /** Set wizard step */
  setZoneWizardStep: (step: number) => void;

  /** Update zone wizard config */
  updateZoneWizardConfig: (config: Partial<ZoneCreateConfig>) => void;

  /** Create zone from wizard config */
  createZoneFromWizard: () => string | null;

  // ============================================================================
  // Actions - Zone Operations
  // ============================================================================

  /** Create a simple box zone */
  createSimpleBoxZone: (config: SimpleBoxConfig) => string;

  /** Create a zone from extruded footprint */
  createExtrudedZone: (config: FootprintExtrudeConfig) => string;

  /** Delete a zone */
  deleteZone: (zoneId: string) => void;

  /** Update zone name */
  updateZoneName: (zoneId: string, name: string) => void;

  // ============================================================================
  // Actions - Surface Operations
  // ============================================================================

  /** Move a surface */
  moveSurface: (config: SurfaceMoveConfig) => void;

  /** Resize a surface */
  resizeSurface: (config: SurfaceResizeConfig) => void;

  /** Delete a surface */
  deleteSurface: (surfaceId: string) => void;

  /** Update surface construction */
  updateSurfaceConstruction: (surfaceId: string, construction: string) => void;

  /** Update surface boundary condition */
  updateSurfaceBoundary: (config: SurfaceBoundaryConfig) => void;

  /** Flip surface normal */
  flipSurfaceNormal: (surfaceId: string) => void;

  // ============================================================================
  // Actions - Fenestration
  // ============================================================================

  /** Open fenestration placer */
  openFenestrationPlacer: (surfaceId: string) => void;

  /** Close fenestration placer */
  closeFenestrationPlacer: () => void;

  /** Update fenestration placer config */
  updateFenestrationConfig: (config: Partial<FenestrationPlaceConfig>) => void;

  /** Update fenestration preview position */
  updateFenestrationPreview: (position: Vector3 | null) => void;

  /** Place fenestration */
  placeFenestration: (config: FenestrationPlaceConfig) => string;

  /** Delete fenestration */
  deleteFenestration: (fenestrationId: string) => void;

  // ============================================================================
  // Actions - Adjacency
  // ============================================================================

  /** Run adjacency detection */
  detectAdjacencies: (zones: ZoneGeometry[]) => void;

  /** Update adjacency detection settings */
  setAdjacencySettings: (settings: Partial<AdjacencyDetectionSettings>) => void;

  /** Link two adjacent surfaces */
  linkAdjacentSurfaces: (surface1Id: string, surface2Id: string) => void;

  /** Unlink adjacent surfaces */
  unlinkAdjacentSurfaces: (surface1Id: string, surface2Id: string) => void;

  /** Set reviewing result index */
  setReviewingAdjacency: (index: number | null) => void;

  /** Clear adjacency results */
  clearAdjacencyResults: () => void;

  // ============================================================================
  // Actions - Construction Builder
  // ============================================================================

  /** Open construction builder */
  openConstructionBuilder: (construction?: ConstructionAssembly) => void;

  /** Close construction builder */
  closeConstructionBuilder: () => void;

  /** Update construction being edited */
  updateEditingConstruction: (updates: Partial<ConstructionAssembly>) => void;

  /** Add layer to construction */
  addConstructionLayer: (layer: Omit<MaterialLayer, 'id'>) => void;

  /** Remove layer from construction */
  removeConstructionLayer: (layerId: string) => void;

  /** Move layer in construction */
  moveConstructionLayer: (layerId: string, direction: 'up' | 'down') => void;

  /** Save construction */
  saveConstruction: () => void;

  /** Load available constructions from IDF */
  loadAvailableConstructions: (constructions: ConstructionAssembly[]) => void;

  /** Load available materials from IDF */
  loadAvailableMaterials: (materials: string[]) => void;

  // ============================================================================
  // Actions - Undo/Redo
  // ============================================================================

  /** Record an action for undo/redo */
  recordAction: (action: Omit<GeometryAction, 'timestamp'>) => void;

  /** Undo last action */
  undo: () => GeometryAction | null;

  /** Redo last undone action */
  redo: () => GeometryAction | null;

  /** Clear undo/redo history */
  clearHistory: () => void;

  // ============================================================================
  // Actions - Notifications
  // ============================================================================

  /** Add notification */
  addNotification: (type: EditorNotification['type'], message: string) => void;

  /** Remove notification */
  removeNotification: (id: string) => void;

  /** Clear all notifications */
  clearNotifications: () => void;

  // ============================================================================
  // Actions - Reset
  // ============================================================================

  /** Reset all editing state */
  reset: () => void;

  /** Mark as clean (no unsaved changes) */
  markClean: () => void;
}

/**
 * Initial editor selection state.
 */
const initialSelection: EditorSelection = {
  zoneIds: [],
  surfaceIds: [],
  fenestrationIds: [],
};

/**
 * Initial drawing state.
 */
const initialDrawing: DrawingState = {
  isDrawing: false,
  vertices: [],
  currentPosition: null,
  isClosed: false,
};

/**
 * Initial zone wizard state.
 */
const initialZoneWizard: ZoneWizardState = {
  isOpen: false,
  step: 0,
  method: 'simple-box',
  config: {},
};

/**
 * Initial fenestration placer state.
 */
const initialFenestrationPlacer: FenestrationPlacerState = {
  isPlacing: false,
  targetSurfaceId: null,
  config: {},
  previewPosition: null,
};

/**
 * Initial adjacency detector state.
 */
const initialAdjacencyDetector: AdjacencyDetectorState = {
  hasRun: false,
  results: [],
  reviewingIndex: null,
  settings: DEFAULT_ADJACENCY_SETTINGS,
};

/**
 * Initial construction builder state.
 */
const initialConstructionBuilder: ConstructionBuilderState = {
  isOpen: false,
  editingConstruction: null,
  isNew: false,
};

/**
 * Geometry editing store.
 */
export const useGeometryEditStore = create<GeometryEditState>()(
  devtools(
    (set, get) => ({
      // Initial state
      editMode: 'none',
      drawingTool: 'select',
      isEditing: false,
      selection: initialSelection,
      isDirty: false,
      drawing: initialDrawing,
      snapSettings: DEFAULT_SNAP_SETTINGS,
      floorLevels: [{ name: 'Level 1', z: 0, defaultHeight: 3 }],
      activeFloorIndex: 0,
      zoneWizard: initialZoneWizard,
      fenestrationPlacer: initialFenestrationPlacer,
      adjacencyDetector: initialAdjacencyDetector,
      constructionBuilder: initialConstructionBuilder,
      availableConstructions: [],
      availableMaterials: [],
      undoStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
      notifications: [],

      // Core actions
      enableEditing: () => {
        set({
          isEditing: true,
          editMode: 'select',
        });
      },

      disableEditing: () => {
        set({
          isEditing: false,
          editMode: 'none',
          drawing: initialDrawing,
          zoneWizard: initialZoneWizard,
          fenestrationPlacer: initialFenestrationPlacer,
        });
      },

      setEditMode: (mode: EditMode) => {
        // Cancel any active drawing when changing modes
        if (mode !== 'footprint-draw') {
          set({
            editMode: mode,
            drawing: initialDrawing,
          });
        } else {
          set({ editMode: mode });
        }
      },

      setDrawingTool: (tool: DrawingTool) => {
        set({ drawingTool: tool });
      },

      setSelection: (selection: Partial<EditorSelection>) => {
        set((state) => ({
          selection: { ...state.selection, ...selection },
        }));
      },

      clearSelection: () => {
        set({ selection: initialSelection });
      },

      addToSelection: (type: 'zone' | 'surface' | 'fenestration', id: string) => {
        set((state) => {
          const key = `${type}Ids` as keyof EditorSelection;
          const current = state.selection[key];
          if (current.includes(id)) return state;
          return {
            selection: {
              ...state.selection,
              [key]: [...current, id],
            },
          };
        });
      },

      removeFromSelection: (type: 'zone' | 'surface' | 'fenestration', id: string) => {
        set((state) => {
          const key = `${type}Ids` as keyof EditorSelection;
          return {
            selection: {
              ...state.selection,
              [key]: state.selection[key].filter((i) => i !== id),
            },
          };
        });
      },

      // Drawing actions
      startDrawing: () => {
        set({
          drawing: {
            isDrawing: true,
            vertices: [],
            currentPosition: null,
            isClosed: false,
          },
        });
      },

      addDrawingVertex: (point: Point2D) => {
        set((state) => ({
          drawing: {
            ...state.drawing,
            vertices: [...state.drawing.vertices, point],
          },
        }));
      },

      updateDrawingPreview: (point: Point2D | null) => {
        set((state) => ({
          drawing: {
            ...state.drawing,
            currentPosition: point,
          },
        }));
      },

      closeDrawing: () => {
        set((state) => ({
          drawing: {
            ...state.drawing,
            isClosed: true,
            isDrawing: false,
          },
        }));
      },

      cancelDrawing: () => {
        set({ drawing: initialDrawing });
      },

      setSnapSettings: (settings: Partial<SnapSettings>) => {
        set((state) => ({
          snapSettings: { ...state.snapSettings, ...settings },
        }));
      },

      setActiveFloor: (index: number) => {
        set({ activeFloorIndex: index });
      },

      addFloorLevel: (floor: FloorLevel) => {
        set((state) => ({
          floorLevels: [...state.floorLevels, floor],
        }));
      },

      // Zone wizard actions
      openZoneWizard: () => {
        set({
          zoneWizard: {
            ...initialZoneWizard,
            isOpen: true,
          },
        });
      },

      closeZoneWizard: () => {
        set({ zoneWizard: initialZoneWizard });
      },

      setZoneWizardStep: (step: number) => {
        set((state) => ({
          zoneWizard: { ...state.zoneWizard, step },
        }));
      },

      updateZoneWizardConfig: (config: Partial<ZoneCreateConfig>) => {
        set((state) => ({
          zoneWizard: {
            ...state.zoneWizard,
            config: { ...state.zoneWizard.config, ...config },
          },
        }));
      },

      createZoneFromWizard: () => {
        const { zoneWizard } = get();
        const { config } = zoneWizard;

        if (config.method === 'simple-box' && config.simpleBox) {
          return get().createSimpleBoxZone(config.simpleBox);
        } else if (config.method === 'extrude-footprint' && config.footprintExtrude) {
          return get().createExtrudedZone(config.footprintExtrude);
        }

        return null;
      },

      // Zone operations
      createSimpleBoxZone: (config: SimpleBoxConfig) => {
        const zoneId = generateId('zone');

        // Record action for undo
        get().recordAction({
          type: 'zone-create',
          description: `Create zone "${config.name}"`,
          data: { config, zoneId },
          inverseData: { zoneId },
          createdIds: [zoneId],
          deletedIds: [],
        });

        set({ isDirty: true });
        get().addNotification('success', `Zone "${config.name}" created`);
        get().closeZoneWizard();

        return zoneId;
      },

      createExtrudedZone: (config: FootprintExtrudeConfig) => {
        const zoneId = generateId('zone');

        // Record action for undo
        get().recordAction({
          type: 'zone-create',
          description: `Create zone "${config.name}" from footprint`,
          data: { config, zoneId },
          inverseData: { zoneId },
          createdIds: [zoneId],
          deletedIds: [],
        });

        set({ isDirty: true });
        get().addNotification('success', `Zone "${config.name}" created from footprint`);
        get().closeZoneWizard();

        return zoneId;
      },

      deleteZone: (zoneId: string) => {
        get().recordAction({
          type: 'zone-delete',
          description: 'Delete zone',
          data: { zoneId },
          inverseData: { zoneId },
          createdIds: [],
          deletedIds: [zoneId],
        });

        set({ isDirty: true });
        get().addNotification('info', 'Zone deleted');
      },

      updateZoneName: (zoneId: string, name: string) => {
        get().recordAction({
          type: 'zone-update',
          description: `Rename zone to "${name}"`,
          data: { zoneId, name },
          inverseData: { zoneId },
          createdIds: [],
          deletedIds: [],
        });

        set({ isDirty: true });
      },

      // Surface operations
      moveSurface: (config: SurfaceMoveConfig) => {
        get().recordAction({
          type: 'surface-move',
          description: 'Move surface',
          data: config as unknown as Record<string, unknown>,
          inverseData: {
            surfaceId: config.surfaceId,
            translation: {
              x: -config.translation.x,
              y: -config.translation.y,
              z: -config.translation.z,
            },
          },
          createdIds: [],
          deletedIds: [],
        });

        set({ isDirty: true });
      },

      resizeSurface: (config: SurfaceResizeConfig) => {
        get().recordAction({
          type: 'surface-resize',
          description: 'Resize surface',
          data: config as unknown as Record<string, unknown>,
          inverseData: { surfaceId: config.surfaceId },
          createdIds: [],
          deletedIds: [],
        });

        set({ isDirty: true });
      },

      deleteSurface: (surfaceId: string) => {
        // Get the object data before deleting (for undo)
        const idfStore = useIDFStore.getState();
        const objectToDelete = idfStore.getObjectById(surfaceId);

        if (!objectToDelete) {
          get().addNotification('error', 'Surface not found');
          return;
        }

        get().recordAction({
          type: 'surface-delete',
          description: 'Delete surface',
          data: { surfaceId, objectData: objectToDelete },
          inverseData: { surfaceId, objectData: objectToDelete },
          createdIds: [],
          deletedIds: [surfaceId],
        });

        // Actually remove from the IDF document
        idfStore.removeObject(surfaceId);

        set({ isDirty: true });
        get().addNotification('info', 'Surface deleted');
      },

      updateSurfaceConstruction: (surfaceId: string, construction: string) => {
        get().recordAction({
          type: 'surface-update',
          description: `Change construction to "${construction}"`,
          data: { surfaceId, construction },
          inverseData: { surfaceId },
          createdIds: [],
          deletedIds: [],
        });

        set({ isDirty: true });
      },

      updateSurfaceBoundary: (config: SurfaceBoundaryConfig) => {
        get().recordAction({
          type: 'surface-update',
          description: `Change boundary condition to "${config.boundaryCondition}"`,
          data: config as unknown as Record<string, unknown>,
          inverseData: { surfaceId: config.surfaceId },
          createdIds: [],
          deletedIds: [],
        });

        set({ isDirty: true });
      },

      flipSurfaceNormal: (surfaceId: string) => {
        get().recordAction({
          type: 'surface-flip',
          description: 'Flip surface normal',
          data: { surfaceId },
          inverseData: { surfaceId },
          createdIds: [],
          deletedIds: [],
        });

        set({ isDirty: true });
      },

      // Fenestration actions
      openFenestrationPlacer: (surfaceId: string) => {
        set({
          fenestrationPlacer: {
            isPlacing: true,
            targetSurfaceId: surfaceId,
            config: {
              surfaceId,
              type: 'window',
              width: 1.2,
              height: 1.5,
              sillHeight: 0.9,
            },
            previewPosition: null,
          },
          editMode: 'fenestration-place',
        });
      },

      closeFenestrationPlacer: () => {
        set({
          fenestrationPlacer: initialFenestrationPlacer,
          editMode: 'select',
        });
      },

      updateFenestrationConfig: (config: Partial<FenestrationPlaceConfig>) => {
        set((state) => ({
          fenestrationPlacer: {
            ...state.fenestrationPlacer,
            config: { ...state.fenestrationPlacer.config, ...config },
          },
        }));
      },

      updateFenestrationPreview: (position: Vector3 | null) => {
        set((state) => ({
          fenestrationPlacer: {
            ...state.fenestrationPlacer,
            previewPosition: position,
          },
        }));
      },

      placeFenestration: (config: FenestrationPlaceConfig) => {
        const fenId = generateId('fen');

        get().recordAction({
          type: 'fenestration-create',
          description: `Place ${config.type} "${config.name}"`,
          data: { config, fenId },
          inverseData: { fenId },
          createdIds: [fenId],
          deletedIds: [],
        });

        set({ isDirty: true });
        get().addNotification('success', `${config.type} placed successfully`);

        return fenId;
      },

      deleteFenestration: (fenestrationId: string) => {
        get().recordAction({
          type: 'fenestration-delete',
          description: 'Delete fenestration',
          data: { fenestrationId },
          inverseData: { fenestrationId },
          createdIds: [],
          deletedIds: [fenestrationId],
        });

        set({ isDirty: true });
        get().addNotification('info', 'Fenestration deleted');
      },

      // Adjacency actions
      detectAdjacencies: (zones: ZoneGeometry[]) => {
        const { settings } = get().adjacencyDetector;
        const results: AdjacencyResult[] = [];

        // Detect adjacent surfaces between zones
        for (let i = 0; i < zones.length; i++) {
          for (let j = i + 1; j < zones.length; j++) {
            const zone1 = zones[i];
            const zone2 = zones[j];
            if (!zone1 || !zone2) continue;

            for (const surface1 of zone1.surfaces) {
              for (const surface2 of zone2.surfaces) {
                const adjacency = checkSurfaceAdjacency(surface1, surface2, settings);
                if (adjacency) {
                  results.push({
                    surface1Id: surface1.id,
                    surface1Name: surface1.name,
                    surface1Zone: zone1.name,
                    surface2Id: surface2.id,
                    surface2Name: surface2.name,
                    surface2Zone: zone2.name,
                    ...adjacency,
                    isLinked:
                      surface1.boundaryCondition === 'surface' &&
                      surface1.outsideBoundaryObject === surface2.name,
                  });
                }
              }
            }
          }
        }

        set({
          adjacencyDetector: {
            ...get().adjacencyDetector,
            hasRun: true,
            results,
            reviewingIndex: results.length > 0 ? 0 : null,
          },
        });

        get().addNotification(
          'info',
          `Found ${results.length} potential adjacencies`
        );
      },

      setAdjacencySettings: (settings: Partial<AdjacencyDetectionSettings>) => {
        set((state) => ({
          adjacencyDetector: {
            ...state.adjacencyDetector,
            settings: { ...state.adjacencyDetector.settings, ...settings },
          },
        }));
      },

      linkAdjacentSurfaces: (surface1Id: string, surface2Id: string) => {
        get().recordAction({
          type: 'adjacency-link',
          description: 'Link adjacent surfaces',
          data: { surface1Id, surface2Id },
          inverseData: { surface1Id, surface2Id },
          createdIds: [],
          deletedIds: [],
        });

        // Update the result to show as linked
        set((state) => ({
          adjacencyDetector: {
            ...state.adjacencyDetector,
            results: state.adjacencyDetector.results.map((r) =>
              (r.surface1Id === surface1Id && r.surface2Id === surface2Id) ||
              (r.surface1Id === surface2Id && r.surface2Id === surface1Id)
                ? { ...r, isLinked: true }
                : r
            ),
          },
          isDirty: true,
        }));

        get().addNotification('success', 'Surfaces linked');
      },

      unlinkAdjacentSurfaces: (surface1Id: string, surface2Id: string) => {
        get().recordAction({
          type: 'adjacency-unlink',
          description: 'Unlink adjacent surfaces',
          data: { surface1Id, surface2Id },
          inverseData: { surface1Id, surface2Id },
          createdIds: [],
          deletedIds: [],
        });

        set((state) => ({
          adjacencyDetector: {
            ...state.adjacencyDetector,
            results: state.adjacencyDetector.results.map((r) =>
              (r.surface1Id === surface1Id && r.surface2Id === surface2Id) ||
              (r.surface1Id === surface2Id && r.surface2Id === surface1Id)
                ? { ...r, isLinked: false }
                : r
            ),
          },
          isDirty: true,
        }));

        get().addNotification('info', 'Surfaces unlinked');
      },

      setReviewingAdjacency: (index: number | null) => {
        set((state) => ({
          adjacencyDetector: {
            ...state.adjacencyDetector,
            reviewingIndex: index,
          },
        }));
      },

      clearAdjacencyResults: () => {
        set((state) => ({
          adjacencyDetector: {
            ...state.adjacencyDetector,
            hasRun: false,
            results: [],
            reviewingIndex: null,
          },
        }));
      },

      // Construction builder actions
      openConstructionBuilder: (construction?: ConstructionAssembly) => {
        set({
          constructionBuilder: {
            isOpen: true,
            editingConstruction: construction || {
              name: 'New Construction',
              type: 'opaque',
              layers: [],
              totalThickness: 0,
              uValue: 0,
              rValue: 0,
            },
            isNew: !construction,
          },
        });
      },

      closeConstructionBuilder: () => {
        set({ constructionBuilder: initialConstructionBuilder });
      },

      updateEditingConstruction: (updates: Partial<ConstructionAssembly>) => {
        set((state) => {
          if (!state.constructionBuilder.editingConstruction) return state;
          return {
            constructionBuilder: {
              ...state.constructionBuilder,
              editingConstruction: {
                ...state.constructionBuilder.editingConstruction,
                ...updates,
              },
            },
          };
        });
      },

      addConstructionLayer: (layer: Omit<MaterialLayer, 'id'>) => {
        const layerId = generateId('layer');
        set((state) => {
          if (!state.constructionBuilder.editingConstruction) return state;
          const newLayer: MaterialLayer = { ...layer, id: layerId };
          const layers = [...state.constructionBuilder.editingConstruction.layers, newLayer];
          const totalThickness = layers.reduce((sum, l) => sum + l.thickness, 0);

          return {
            constructionBuilder: {
              ...state.constructionBuilder,
              editingConstruction: {
                ...state.constructionBuilder.editingConstruction,
                layers,
                totalThickness,
              },
            },
          };
        });
      },

      removeConstructionLayer: (layerId: string) => {
        set((state) => {
          if (!state.constructionBuilder.editingConstruction) return state;
          const layers = state.constructionBuilder.editingConstruction.layers.filter(
            (l) => l.id !== layerId
          );
          const totalThickness = layers.reduce((sum, l) => sum + l.thickness, 0);

          return {
            constructionBuilder: {
              ...state.constructionBuilder,
              editingConstruction: {
                ...state.constructionBuilder.editingConstruction,
                layers,
                totalThickness,
              },
            },
          };
        });
      },

      moveConstructionLayer: (layerId: string, direction: 'up' | 'down') => {
        set((state) => {
          if (!state.constructionBuilder.editingConstruction) return state;
          const layers = [...state.constructionBuilder.editingConstruction.layers];
          const index = layers.findIndex((l) => l.id === layerId);
          if (index === -1) return state;

          const newIndex = direction === 'up' ? index - 1 : index + 1;
          if (newIndex < 0 || newIndex >= layers.length) return state;

          // Swap
          const layer = layers[index];
          if (layer) {
            layers.splice(index, 1);
            layers.splice(newIndex, 0, layer);
          }

          return {
            constructionBuilder: {
              ...state.constructionBuilder,
              editingConstruction: {
                ...state.constructionBuilder.editingConstruction,
                layers,
              },
            },
          };
        });
      },

      saveConstruction: () => {
        const { constructionBuilder } = get();
        if (!constructionBuilder.editingConstruction) return;

        const actionType = constructionBuilder.isNew ? 'construction-create' : 'construction-update';
        get().recordAction({
          type: actionType,
          description: `${constructionBuilder.isNew ? 'Create' : 'Update'} construction "${constructionBuilder.editingConstruction.name}"`,
          data: { construction: constructionBuilder.editingConstruction },
          inverseData: { construction: constructionBuilder.editingConstruction },
          createdIds: constructionBuilder.isNew ? [constructionBuilder.editingConstruction.name] : [],
          deletedIds: [],
        });

        set({ isDirty: true });
        get().addNotification(
          'success',
          `Construction "${constructionBuilder.editingConstruction.name}" saved`
        );
        get().closeConstructionBuilder();
      },

      loadAvailableConstructions: (constructions: ConstructionAssembly[]) => {
        set({ availableConstructions: constructions });
      },

      loadAvailableMaterials: (materials: string[]) => {
        set({ availableMaterials: materials });
      },

      // Undo/Redo actions
      recordAction: (action: Omit<GeometryAction, 'timestamp'>) => {
        const fullAction: GeometryAction = {
          ...action,
          timestamp: Date.now(),
        };

        set((state) => {
          const undoStack = [...state.undoStack, fullAction];
          // Trim stack if too large
          if (undoStack.length > MAX_HISTORY_SIZE) {
            undoStack.shift();
          }

          return {
            undoStack,
            redoStack: [], // Clear redo stack on new action
            canUndo: true,
            canRedo: false,
          };
        });
      },

      undo: () => {
        const { undoStack } = get();
        if (undoStack.length === 0) return null;

        const action = undoStack[undoStack.length - 1];
        if (!action) return null;

        set((state) => {
          const newUndoStack = state.undoStack.slice(0, -1);
          const newRedoStack = [...state.redoStack, action];

          return {
            undoStack: newUndoStack,
            redoStack: newRedoStack,
            canUndo: newUndoStack.length > 0,
            canRedo: true,
          };
        });

        get().addNotification('info', `Undid: ${action.description}`);
        return action;
      },

      redo: () => {
        const { redoStack } = get();
        if (redoStack.length === 0) return null;

        const action = redoStack[redoStack.length - 1];
        if (!action) return null;

        set((state) => {
          const newRedoStack = state.redoStack.slice(0, -1);
          const newUndoStack = [...state.undoStack, action];

          return {
            undoStack: newUndoStack,
            redoStack: newRedoStack,
            canUndo: true,
            canRedo: newRedoStack.length > 0,
          };
        });

        get().addNotification('info', `Redid: ${action.description}`);
        return action;
      },

      clearHistory: () => {
        set({
          undoStack: [],
          redoStack: [],
          canUndo: false,
          canRedo: false,
        });
      },

      // Notification actions
      addNotification: (type: EditorNotification['type'], message: string) => {
        const notification: EditorNotification = {
          id: generateId('notif'),
          type,
          message,
          timestamp: Date.now(),
        };

        set((state) => ({
          notifications: [...state.notifications, notification],
        }));

        // Auto-remove after 5 seconds
        setTimeout(() => {
          get().removeNotification(notification.id);
        }, 5000);
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      // Reset
      reset: () => {
        set({
          editMode: 'none',
          drawingTool: 'select',
          isEditing: false,
          selection: initialSelection,
          isDirty: false,
          drawing: initialDrawing,
          snapSettings: DEFAULT_SNAP_SETTINGS,
          floorLevels: [{ name: 'Level 1', z: 0, defaultHeight: 3 }],
          activeFloorIndex: 0,
          zoneWizard: initialZoneWizard,
          fenestrationPlacer: initialFenestrationPlacer,
          adjacencyDetector: initialAdjacencyDetector,
          constructionBuilder: initialConstructionBuilder,
          undoStack: [],
          redoStack: [],
          canUndo: false,
          canRedo: false,
          notifications: [],
        });
      },

      markClean: () => {
        set({ isDirty: false });
      },
    }),
    { name: 'GeometryEditStore' }
  )
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if two surfaces are adjacent.
 */
function checkSurfaceAdjacency(
  surface1: SurfaceGeometry,
  surface2: SurfaceGeometry,
  settings: AdjacencyDetectionSettings
): Omit<AdjacencyResult, 'surface1Id' | 'surface2Id' | 'surface1Name' | 'surface2Name' | 'surface1Zone' | 'surface2Zone' | 'isLinked'> | null {
  // Calculate centers
  const center1 = calculateCenter(surface1.vertices);
  const center2 = calculateCenter(surface2.vertices);

  // Check distance between centers
  const centerDistance = distance3D(center1, center2);
  if (centerDistance > settings.maxDistance + 5) {
    return null;
  }

  // Check normal alignment (should be opposite for adjacent surfaces)
  const normalAlignment = -dotProduct(surface1.normal, surface2.normal);
  if (normalAlignment < settings.minNormalAlignment) {
    return null;
  }

  // Calculate overlap area
  const overlapArea = calculateOverlapArea(surface1.vertices, surface2.vertices, surface1.normal);
  if (overlapArea < settings.minOverlapArea) {
    return null;
  }

  // Determine match quality
  let matchQuality: 'exact' | 'close' | 'partial';
  if (normalAlignment > 0.99 && centerDistance < settings.maxDistance) {
    matchQuality = 'exact';
  } else if (normalAlignment > 0.95) {
    matchQuality = 'close';
  } else {
    matchQuality = 'partial';
  }

  // Determine suggested action
  let suggestedAction: 'link' | 'merge' | 'review';
  if (matchQuality === 'exact') {
    suggestedAction = 'link';
  } else if (matchQuality === 'close') {
    suggestedAction = 'link';
  } else {
    suggestedAction = 'review';
  }

  return {
    matchQuality,
    overlapArea,
    normalAlignment,
    centerDistance,
    suggestedAction,
  };
}

/**
 * Calculate center of vertices.
 */
function calculateCenter(vertices: Vector3[]): Vector3 {
  if (vertices.length === 0) return { x: 0, y: 0, z: 0 };

  const sum = vertices.reduce(
    (acc, v) => ({ x: acc.x + v.x, y: acc.y + v.y, z: acc.z + v.z }),
    { x: 0, y: 0, z: 0 }
  );

  return {
    x: sum.x / vertices.length,
    y: sum.y / vertices.length,
    z: sum.z / vertices.length,
  };
}

/**
 * Calculate 3D distance between two points.
 */
function distance3D(p1: Vector3, p2: Vector3): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dz = p2.z - p1.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate dot product of two vectors.
 */
function dotProduct(v1: Vector3, v2: Vector3): number {
  return v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
}

/**
 * Calculate approximate overlap area between two surfaces.
 */
function calculateOverlapArea(
  vertices1: Vector3[],
  vertices2: Vector3[],
  _normal: Vector3
): number {
  // Simplified overlap calculation
  // Project both surfaces onto the plane defined by the normal
  // and calculate the intersection area

  // For now, use the minimum area of the two surfaces as an approximation
  const area1 = calculateSurfaceArea(vertices1);
  const area2 = calculateSurfaceArea(vertices2);

  // Check if surfaces are close enough to potentially overlap
  const center1 = calculateCenter(vertices1);
  const center2 = calculateCenter(vertices2);
  const dist = distance3D(center1, center2);

  // If centers are far apart, no overlap
  const maxDim = Math.max(
    Math.sqrt(area1),
    Math.sqrt(area2)
  );
  if (dist > maxDim) return 0;

  // Approximate overlap as the smaller area scaled by proximity
  return Math.min(area1, area2) * Math.max(0, 1 - dist / maxDim);
}

/**
 * Calculate the area of a surface defined by vertices.
 */
function calculateSurfaceArea(vertices: Vector3[]): number {
  if (vertices.length < 3) return 0;

  let area = 0;
  const n = vertices.length;

  for (let i = 1; i < n - 1; i++) {
    const v0 = vertices[0];
    const v1 = vertices[i];
    const v2 = vertices[i + 1];
    if (!v0 || !v1 || !v2) continue;

    // Triangle area using cross product
    const ax = v1.x - v0.x;
    const ay = v1.y - v0.y;
    const az = v1.z - v0.z;
    const bx = v2.x - v0.x;
    const by = v2.y - v0.y;
    const bz = v2.z - v0.z;

    const cx = ay * bz - az * by;
    const cy = az * bx - ax * bz;
    const cz = ax * by - ay * bx;

    area += Math.sqrt(cx * cx + cy * cy + cz * cz) / 2;
  }

  return area;
}

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Hook to get the current edit mode.
 */
export const useEditMode = () => useGeometryEditStore((state) => state.editMode);

/**
 * Hook to check if editing is enabled.
 */
export const useIsEditing = () => useGeometryEditStore((state) => state.isEditing);

/**
 * Hook to get selection state.
 */
export const useSelection = () => useGeometryEditStore((state) => state.selection);

/**
 * Hook to get drawing state.
 */
export const useDrawingState = () => useGeometryEditStore((state) => state.drawing);

/**
 * Hook to get zone wizard state.
 */
export const useZoneWizard = () => useGeometryEditStore((state) => state.zoneWizard);

/**
 * Hook to get fenestration placer state.
 */
export const useFenestrationPlacer = () => useGeometryEditStore((state) => state.fenestrationPlacer);

/**
 * Hook to get adjacency detector state.
 */
export const useAdjacencyDetector = () => useGeometryEditStore((state) => state.adjacencyDetector);

/**
 * Hook to get construction builder state.
 */
export const useConstructionBuilder = () => useGeometryEditStore((state) => state.constructionBuilder);

/**
 * Hook to get undo/redo state.
 */
export const useUndoRedo = () => useGeometryEditStore(
  useShallow((state) => ({
    canUndo: state.canUndo,
    canRedo: state.canRedo,
    undo: state.undo,
    redo: state.redo,
  }))
);

/**
 * Hook to get notifications.
 */
export const useNotifications = () => useGeometryEditStore((state) => state.notifications);
